import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { Autocomplete, Button, CircularProgress, Collapse, Stack, styled, TextField, Typography } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import { assetService } from "../../../Core/Api/services";
import {
  AccessGroupInfos,
  AccessUserInfos,
  UsersAndGroupsByPermissionCombination,
} from "../../../Core/Api/Types/access-models";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { PermissionMode } from "../../../Core/Api/Types/permission-mode";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import { SetSpecificPermissionsRequest } from "../../../Core/Api/Types/set-specific-permissions-request";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import AccessCustomChip from "../../Access/Shared/access-custom-chip";
import AccessCustomChipsSummary from "../../Access/Shared/access-custom-chips-summary";
import AssetAndChildrenCard from "../../Access/Shared/asset-and-children-card";
import PermissionsFormSection from "../../Access/Shared/permissions-form-section";
import FormSection from "../../Forms/FormSection/form-section";
import { useTranslation } from "react-i18next";

type OptionBase = {
  type: "user" | "group";
};

type UserOption = OptionBase & {
  type: "user";
  user: AccessUserInfos;
};

type GroupOption = OptionBase & {
  type: "group";
  group: AccessGroupInfos;
};

type Option = UserOption | GroupOption;

function isUserOption(option: Option): option is UserOption {
  return option.type === "user";
}

function isGroupOption(option: Option): option is GroupOption {
  return option.type === "group";
}

function getUserName(user: AccessUserInfos) {
  return `${user.firstName} ${user.lastName}`;
}

function getGroupName(group: AccessGroupInfos) {
  return `${group.name} (${group.count})`;
}

export type ModalCreateOrUpdatePermsElementProps = {
  asset: DbAssetObjectInfos;
  availableUserInfos: AccessUserInfos[];
  availableGroupInfos: AccessGroupInfos[];
  updateCombination?: UsersAndGroupsByPermissionCombination;
  onSuccess: () => void;
};

export type ModalCreateOrUpdatePermsProps = CommonModalProps<ModalCreateOrUpdatePermsElementProps>;

const ModalCreateOrUpdatePerms = React.forwardRef<any, ModalCreateOrUpdatePermsProps>((props, ref) => {
  const { t } = useTranslation();
  const {
    asset: _asset,
    availableUserInfos: _availableUserInfos,
    availableGroupInfos: _availableGroupInfos,
    updateCombination,
    onSuccess,
    closeModal,
  } = props;

  const enqueueSnackbar = useCustomSnackbar();

  const { active: emailsExpanded, toggle: toggleEmailsExpanded } = useToggle(true);

  const [asset, setAsset] = React.useState<DbAssetObjectInfos>(_asset);
  const [isWaitingCall, setIsWaitingCall] = React.useState(false);
  const [selectedPermissions, setSelectedPermissions] = React.useState<PermissionType[]>(
    updateCombination?.permissionCombination.filter((x) => x.mode === PermissionMode.Allow).map((x) => x.permission) ||
      []
  );
  const [selectedOptions, setSelectedOptions] = React.useState<Option[]>([]);

  // #region memos
  const availableUserInfos = React.useMemo(() => {
    const usersToAdd: AccessUserInfos[] = [];
    if (updateCombination) usersToAdd.push(...updateCombination.users);

    return [..._availableUserInfos, ...usersToAdd];
  }, [_availableUserInfos, updateCombination]);

  const availableGroupInfos = React.useMemo(() => {
    const groupsToAdd: AccessGroupInfos[] = [];
    if (updateCombination) groupsToAdd.push(...updateCombination.groups);

    return [..._availableGroupInfos, ...groupsToAdd];
  }, [_availableGroupInfos, updateCombination]);

  const selectionOptions: Option[] = React.useMemo(() => {
    const output: Option[] = [];

    availableGroupInfos
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((group) => {
        output.push({ type: "group", group });
        const usersInGroup = availableUserInfos
          .filter((user) => user.groupId === group.id)
          .sort((a, b) => {
            const lastNameComparison = a.lastName?.localeCompare(b.lastName);
            if (lastNameComparison !== 0) {
              return lastNameComparison;
            }
            return a.firstName?.localeCompare(b.firstName);
          });
        usersInGroup.forEach((user) => {
          output.push({ type: "user", user });
        });
      });

    return output;
  }, [availableUserInfos, availableGroupInfos]);

  const selectedGroups: AccessGroupInfos[] = React.useMemo(() => {
    return selectedOptions.filter(isGroupOption).map((option) => option.group);
  }, [selectedOptions]);

  const selectedUsers: AccessUserInfos[] = React.useMemo(() => {
    const selectedGroupsIds = selectedGroups.map((group) => group.id);
    return selectedOptions
      .filter(isUserOption)
      .filter((u) => !selectedGroupsIds.includes(u.user.groupId))
      .map((option) => option.user);
  }, [selectedOptions, selectedGroups]);

  const totalUsersCount = React.useMemo(() => {
    return selectedUsers.length + selectedGroups.reduce((acc, group) => acc + group.count, 0);
  }, [selectedUsers, selectedGroups]);
  // #endregion memos

  // #region handlers
  const handleCancelClick = () => {
    closeModal();
  };
  // Permissions
  const handlePermissionClick = (permission: PermissionType) => {
    if (selectedPermissions.includes(permission)) {
      setSelectedPermissions(selectedPermissions.filter((p) => p !== permission));
    } else {
      setSelectedPermissions([...selectedPermissions, permission]);
    }
  };

  // Users and groups
  const customGetOptionLabel = (option: Option) => {
    if (option.type === "user") {
      return `${option.user.firstName} ${option.user.lastName}`;
    } else {
      return option.group.name;
    }
  };

  const handleUserSelectionChange = (event: React.SyntheticEvent, value: Option[]) => {
    // Get selected groups
    const groupOptions: GroupOption[] = value.filter(isGroupOption);

    // Add groups to value
    const newValue: Option[] = [...groupOptions];

    // Add users from selected groups to value
    groupOptions.forEach((groupOption) => {
      const usersInGroup = selectionOptions.filter(
        (option) => option.type === "user" && option.user.groupId === groupOption.group.id
      );
      newValue.push(...usersInGroup);
    });

    // Get previous group options
    const previousGroupOptionsIds = selectedOptions.filter(isGroupOption).map((option) => option.group.id);

    // Handled users are users that are already in the value
    const handledUserIds = newValue.filter(isUserOption).map((option) => option.user.id);
    // Add users that are not in the value and are not in the previous group options => they are selected users, not from groups
    newValue.push(
      ...value.filter(
        (option) =>
          option.type === "user" &&
          !handledUserIds.includes(option.user.id) &&
          !previousGroupOptionsIds.includes(option.user.groupId)
      )
    );

    setSelectedOptions(newValue);
  };

  const customGetOptionDisabled = (option: Option) => {
    if (
      option.type === "user" &&
      selectedOptions.find(
        (selectedOption) => selectedOption.type === "group" && selectedOption.group.id === option.user.groupId
      )
    ) {
      return true;
    }
    return false;
  };

  const customIsOptionEqualtToValue = (option: Option, value: Option) => {
    if (isGroupOption(option) && isGroupOption(value)) {
      return option.group.id === value.group.id;
    } else if (isUserOption(option) && isUserOption(value)) {
      return option.user.id === value.user.id;
    }

    return false;
  };

  const unselectGroup = (groupId: number) => {
    setSelectedOptions(
      selectedOptions.filter(
        (option) =>
          (isUserOption(option) && option.user.groupId !== groupId) ||
          (isGroupOption(option) && option.group.id !== groupId)
      )
    );
  };

  const unselectUser = (userId: number) => {
    setSelectedOptions(selectedOptions.filter((option) => isGroupOption(option) || option.user.id !== userId));
  };

  const handleConfirmClick = () => {
    setIsWaitingCall(true);

    const selectedUserIds = selectedUsers.map((u) => u.id);
    const selectedGroupIds = selectedGroups.map((g) => g.id);

    const request: SetSpecificPermissionsRequest = {
      objectId: asset.id,
      allowedPermissions: selectedPermissions,
      userIds: selectedUserIds,
      groupIds: selectedGroupIds,
      removeGroupIds: [],
      removeUserIds: [],
    };

    if (updateCombination) {
      request.removeUserIds = updateCombination.users.filter((u) => !selectedUserIds.includes(u.id)).map((u) => u.id);
      request.removeGroupIds = updateCombination.groups
        .filter((g) => !selectedGroupIds.includes(g.id))
        .map((g) => g.id);
    }

    assetService
      .SetSpecificPermissions(request)
      .then((reponse) => {
        if (reponse.success) {
          enqueueSnackbar(t("alert:success.usersAccessesUpdated"), "success");
          onSuccess();
          closeModal();
        } else enqueueSnackbar(t("alert:error.updatingUsersAccesses"), "error");
      })
      .finally(() => {
        setIsWaitingCall(false);
      });
  };
  // #endregion handlers

  React.useEffect(() => {
    if (updateCombination) {
      setSelectedOptions([
        ...selectionOptions.filter(
          (option) =>
            (isGroupOption(option) && updateCombination.groups.some((group) => group.id === option.group.id)) ||
            (isUserOption(option) && updateCombination.users.some((user) => user.id === option.user.id))
        ),
      ]);
    }
  }, [asset.id, enqueueSnackbar, selectionOptions, updateCombination]);

  return (
    <Modal
      title={updateCombination ? t("modal:title.updatePermissions") : t("modal:title.createPermissions")}
      reference={ref}
      closeModal={handleCancelClick}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button variant="contained" onClick={handleConfirmClick} disabled={totalUsersCount === 0 || isWaitingCall}>
            {isWaitingCall ? (
              <CircularProgress size={20} color="secondary" />
            ) : (
              t("button:createPermissionsForUser", { count: totalUsersCount })
            )}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <AssetAndChildrenCard asset={asset} />
        <PermissionsFormSection
          selectedPermissions={selectedPermissions}
          handlePermissionClick={handlePermissionClick}
          variant="accent"
        />
        <FormSection
          title={t("form:section.title.usersAndGroups")}
          expanded={emailsExpanded}
          onExpand={toggleEmailsExpanded}
          icon={<GroupOutlinedIcon />}
          status={"custom"}
          statusText={t("common:configurationRequired")}
          helperText={t("form:helper.atLeastOneEmailIsRequired")}
          sx={{ mb: 10 }}
          customStatus={
            <Collapse in={!emailsExpanded}>
              <Stack spacing={0.5}>
                <AccessCustomChipsSummary
                  labels={selectedGroups.map(getGroupName)}
                  chipsIcon={<GroupOutlinedIcon />}
                  chipsColor="accent"
                  nbToShow={3}
                />
                <AccessCustomChipsSummary labels={selectedUsers.map(getUserName)} chipsColor="accent" nbToShow={3} />
              </Stack>
            </Collapse>
          }
        >
          <Stack spacing={2}>
            {(selectedUsers.length > 0 || selectedGroups.length > 0) && (
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" sx={{ flexWrap: "wrap", gap: 0.5 }}>
                  {selectedGroups.map((group, index) => (
                    <AccessCustomChip
                      key={index}
                      label={getGroupName(group)}
                      color="accent"
                      icon={<GroupOutlinedIcon />}
                      onDelete={() => unselectGroup(group.id)}
                    />
                  ))}
                </Stack>
                <Stack direction="row" alignItems="center" sx={{ flexWrap: "wrap", gap: 0.5 }}>
                  {selectedUsers.map((user, index) => (
                    <AccessCustomChip
                      key={index}
                      label={getUserName(user)}
                      color="accent"
                      onDelete={() => unselectUser(user.id)}
                    />
                  ))}
                </Stack>
              </Stack>
            )}

            <Stack spacing={1}>
              <Autocomplete
                multiple
                options={selectionOptions}
                value={selectedOptions}
                onChange={handleUserSelectionChange}
                getOptionLabel={customGetOptionLabel}
                getOptionDisabled={customGetOptionDisabled}
                isOptionEqualToValue={customIsOptionEqualtToValue}
                renderOption={(props, option, { index }) => (
                  <CustomOption key={index} defaultProps={props} option={option} />
                )}
                renderTags={() => null}
                disableCloseOnSelect
                disableClearable
                popupIcon={<ExpandMoreRoundedIcon />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("form:section.title.usersAndGroups")}
                    placeholder={t("field:placeholder.searchGroupOrUser")}
                  />
                )}
              />
              <Typography variant="body2" color="text.secondary">
                {t("component:body.groupOrIndividualSelectOption")}
              </Typography>
            </Stack>
          </Stack>
        </FormSection>
      </Stack>
    </Modal>
  );
});

const CustomOption = (props: { defaultProps: React.HTMLAttributes<HTMLLIElement>; option: Option }) => {
  const { defaultProps, option } = props;

  return (
    <>
      {option.type === "user" ? (
        <CustomListItem
          {...defaultProps}
          type={option.type}
        >{`${option.user.firstName} ${option.user.lastName}`}</CustomListItem>
      ) : (
        <CustomListItem {...defaultProps} type={option.type}>
          {option.group.name}
        </CustomListItem>
      )}
    </>
  );
};

const CustomListItem = styled("li", { shouldForwardProp: (prop) => prop !== "type" && prop !== "paddingLeft" })<{
  type: Option["type"];
}>(({ theme, type }) => ({
  marginLeft: type === "group" ? theme.spacing(0) : theme.spacing(1),
  fontWeight: type === "group" ? "bold" : "normal",
  color: type === "group" ? theme.palette.text.tier : theme.palette.text.primary,
  backgroundColor: type === "group" ? theme.palette.background.paper : "transparent",
}));

export default ModalCreateOrUpdatePerms;
